/**
 * Pure, DOM-free geometry for the TurnMetaDrawer (model-meta-hardening Phase 2).
 *
 * The drawer is anchored at the y-coordinate of the tapped model/cost/time badge.
 * The old code clamped `top = min(anchorY, vh - 320)` but the panel's max-height was a
 * STATIC CSS cap (`calc(100vh - 80px)`), decoupled from the runtime `top`. When the badge
 * sat low in the viewport, `top` was pushed up to `vh - 320` yet the panel could still be
 * ~400px tall → it overflowed BELOW the fold, invisible (the exact #690/#1196 bug).
 *
 * `computeDrawerBox` makes the box reactive and always fully on-screen:
 *  - When the badge is in the bottom third of the viewport, the anchor flips UPWARD so the
 *    box grows up from the badge instead of down off the bottom edge.
 *  - `maxHeight` is whatever fits between `top` and `vh - margin` (capped at the measured
 *    content height so a short drawer doesn't reserve dead space).
 *  - The result is ALWAYS inside `[margin, vh - margin]`.
 *
 * Pure ⇒ unit-testable headless (see setpoint-evals/turn-meta-registry-drawer/SE-01).
 */
export interface DrawerBox {
  /** Distance from the top of the viewport to the top edge of the panel (px). */
  top: number;
  /** Reactive max-height for the panel (px) — never exceeds `vh - 2*margin`. */
  maxHeight: number;
}

export interface ComputeDrawerBoxOpts {
  /** Top/bottom safe margin kept clear of the viewport edges (px). Default 16. */
  margin?: number;
}

export function computeDrawerBox(
  anchorY: number,
  vh: number,
  contentH: number,
  opts: ComputeDrawerBoxOpts = {},
): DrawerBox {
  const margin = opts.margin ?? 16;

  // Degenerate viewport (SSR/zero) — return a safe minimal box.
  if (!Number.isFinite(vh) || vh <= 2 * margin) {
    return { top: margin, maxHeight: Math.max(0, vh - 2 * margin) };
  }

  // The vertical band the panel may occupy.
  const minTop = margin;
  const maxBottom = vh - margin;
  const available = maxBottom - minTop; // total usable height

  // Clamp the requested content height into the usable band.
  const desiredH = Math.max(0, Math.min(contentH, available));

  // Sanitize the anchor into the viewport.
  const anchor = Number.isFinite(anchorY) ? Math.max(0, Math.min(anchorY, vh)) : vh / 2;

  // Flip upward when the badge sits in the bottom third: grow the box UP from the anchor
  // so it never runs off the bottom edge.
  const bottomThirdStart = vh * (2 / 3);
  let top: number;
  if (anchor >= bottomThirdStart) {
    // Anchor the BOTTOM of the box at the badge, grow upward.
    top = anchor - desiredH;
  } else {
    // Anchor the TOP of the box at the badge, grow downward.
    top = anchor;
  }

  // Final clamp: the whole box must live inside [minTop, maxBottom].
  if (top < minTop) top = minTop;
  if (top + desiredH > maxBottom) top = maxBottom - desiredH;
  if (top < minTop) top = minTop; // re-clamp if desiredH == available

  // maxHeight is whatever fits below `top`, capped at the desired content height.
  const maxHeight = Math.min(desiredH, maxBottom - top);

  return { top, maxHeight };
}
